import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Bell, BellOff, Loader2, X } from 'lucide-react';

// Convert base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushNotificationManager = () => {
  const { api } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permission, setPermission] = useState('default');
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    checkSupport();
  }, []);

  const checkSupport = async () => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      
      try {
        // Register service worker
        const registration = await navigator.serviceWorker.register('/sw.js');
        
        // Check existing subscription
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          setIsSubscribed(true);
        } else {
          // Show banner if not subscribed and not denied
          const dismissed = localStorage.getItem('push_banner_dismissed');
          if (!dismissed && Notification.permission !== 'denied') {
            setShowBanner(true);
          }
        }
        
        // Also check backend status
        try {
          const status = await api.get('/push/status');
          setIsSubscribed(status.data.subscribed);
        } catch (e) {
          // Ignore if endpoint doesn't exist
        }
      } catch (error) {
        console.error('Error checking push status:', error);
      }
    }
    
    setIsLoading(false);
  };

  const handleSubscribe = async () => {
    setIsLoading(true);
    
    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        toast.error('Permission refusée pour les notifications');
        setIsLoading(false);
        return;
      }
      
      // Get VAPID public key from backend
      let vapidPublicKey;
      try {
        const vapidResponse = await api.get('/push/vapid-public-key');
        vapidPublicKey = vapidResponse.data.publicKey;
      } catch (e) {
        console.error('Error getting VAPID key:', e);
        toast.error('Erreur serveur - réessayez plus tard');
        setIsLoading(false);
        return;
      }
      
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push
      let subscription;
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });
      } catch (e) {
        console.error('Error subscribing to push manager:', e);
        toast.error('Erreur d\'inscription aux notifications');
        setIsLoading(false);
        return;
      }
      
      // Send subscription to backend
      try {
        await api.post('/push/subscribe', {
          subscription: subscription.toJSON()
        });
      } catch (e) {
        console.error('Error saving subscription:', e);
        toast.error('Erreur de sauvegarde - notifications non activées');
        setIsLoading(false);
        return;
      }
      
      setIsSubscribed(true);
      setShowBanner(false);
      toast.success('Notifications activées !');
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error('Erreur inattendue');
    }
    
    setIsLoading(false);
  };

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem('push_banner_dismissed', 'true');
  };

  // Don't render anything if not supported, already subscribed, or banner dismissed
  if (!isSupported || isSubscribed || !showBanner || isLoading) {
    return null;
  }

  // Render a subtle floating banner at the bottom
  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-fade-in">
      <div className="bg-card/95 backdrop-blur-sm border border-border rounded-xl p-4 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">Activer les notifications ?</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Soyez alerté des nouveaux messages et événements
            </p>
          </div>
          <button 
            onClick={dismissBanner}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <Button 
            variant="ghost" 
            size="sm" 
            className="flex-1"
            onClick={dismissBanner}
          >
            Plus tard
          </Button>
          <Button 
            size="sm" 
            className="flex-1"
            onClick={handleSubscribe}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Activer'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationManager;
